<template>
  <section class="vp-card profile-reviews-card">
    <div class="vp-card-head">
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-star"></i>
        {{ $t('profile.reviews.title') }}
        <span v-if="commentsTotalCount > 0" class="vp-chip">{{ $formatInteger(commentsTotalCount) }}</span>
      </h6>
      <div v-if="showAverageInHeader && !hasSummaryStats && averageScore != null" class="d-flex align-items-center gap-2">
        <span class="vp-text-muted small">{{ $t('profile.reviews.average') }}</span>
        <star-rating-display :score-one-to-ten="averageScore" small />
      </div>
    </div>

    <div class="vp-card-body">
      <div v-if="isLoading" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status" />
      </div>
      <div v-else-if="loadError" class="vp-note vp-note--danger">{{ loadError }}</div>
      <template v-else>
        <!-- Write a review (buyer viewing a supplier they have not reviewed yet) -->
        <div v-if="canAddReview && !hasOwnTopLevelReview" class="rc-compose">
          <div class="rc-compose-title">{{ $t('profile.reviews.addTitle') }}</div>
          <span class="vp-label">{{ $t('profile.reviews.ratingLabel') }}</span>
          <div class="rc-rating-input">
            <button
              v-for="step in ratingSteps"
              :key="step"
              type="button"
              class="rc-star-btn"
              :class="{ 'is-active': newRating >= step }"
              :aria-label="$t('profile.reviews.ratingAria', { value: step })"
              :aria-pressed="newRating === step"
              @click="newRating = step"
            >
              <i class="ph-duotone ph-star"></i>
            </button>
            <span class="rc-rating-value">{{ newRating }}/5</span>
          </div>
          <label class="vp-label" for="rc-new-comment">{{ $t('profile.reviews.commentLabel') }}</label>
          <textarea
            id="rc-new-comment"
            v-model="newComment"
            class="vp-control rc-textarea"
            rows="3"
            :placeholder="$t('profile.reviews.commentPlaceholder')"
            :disabled="submittingReview"
          />
          <div class="rc-actions">
            <button
              type="button"
              class="vp-btn vp-btn-primary vp-btn-sm"
              :disabled="submittingReview || newRating < 1 || !newComment.trim()"
              @click="submitNewReview"
            >
              <span v-if="submittingReview" class="spinner-border spinner-border-sm" />
              {{ $t('profile.reviews.submitReview') }}
            </button>
          </div>
        </div>

        <div v-if="!hasSummaryStats && topLevelReviews.length === 0" class="vp-empty">
          <i class="ph-duotone ph-star vp-empty-icon"></i>
          <div class="vp-empty-text">{{ $t('profile.reviews.empty') }}</div>
        </div>
        <template v-else>
          <div class="rc-toolbar">
            <label class="vp-label mb-0" for="rc-sort">{{ $t('profile.reviews.sort.label') }}</label>
            <select id="rc-sort" v-model="sortBy" class="vp-select vp-select--sm">
              <option value="date_desc">{{ $t('profile.reviews.sort.newest') }}</option>
              <option value="date_asc">{{ $t('profile.reviews.sort.oldest') }}</option>
              <option value="rating_desc">{{ $t('profile.reviews.sort.ratingDesc') }}</option>
              <option value="rating_asc">{{ $t('profile.reviews.sort.ratingAsc') }}</option>
            </select>
          </div>

          <div v-if="commentsLoading && !isLoading" class="vp-loading">
            <span class="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
          <div v-else-if="topLevelReviews.length === 0" class="vp-empty py-3">
            <div class="vp-empty-text mt-0">{{ $t('profile.reviews.emptyRecent') }}</div>
          </div>
          <div v-else class="rc-list">
            <template v-for="(rev, idx) in displayedReviews" :key="rev.id">
              <!-- A buyer's own review is pinned first; the rest follow under their own label. -->
              <div v-if="rev.isPinned" class="rc-section-label">{{ $t('profile.reviews.yourReview') }}</div>
              <div v-else-if="pinnedOwnReview && idx === 1" class="rc-section-label rc-section-label--split">
                {{ $t('profile.reviews.otherReviews') }}
              </div>

              <article class="rc-item" :class="{ 'is-own': rev.isOwnReview }">
                <div class="rc-item-head">
                  <span class="rc-avatar">{{ getInitials(rev.reviewerLabel) }}</span>
                  <div class="rc-item-who">
                    <div class="rc-buyer">
                      <span class="vp-truncate">{{ rev.reviewerLabel }}</span>
                      <span v-if="rev.isOwnReview" class="vp-chip vp-chip--primary">{{ $t('profile.reviews.yourReview') }}</span>
                    </div>
                    <star-rating-display v-if="rev.rating != null" :score-one-to-ten="rev.rating" small />
                    <span v-else class="vp-text-muted small">—</span>
                  </div>
                  <div class="rc-item-meta">
                    <span class="rc-date">{{ rev.createdAtDisplay }}</span>
                    <div v-if="rev.isOwnReview" class="rc-item-actions">
                      <button
                        type="button"
                        class="vp-icon-btn"
                        :title="$t('common.actions.edit')"
                        :aria-label="$t('common.actions.edit')"
                        @click="editingReviewId = rev.id"
                      >
                        <i class="ph-duotone ph-pencil-simple"></i>
                      </button>
                      <button
                        type="button"
                        class="vp-icon-btn vp-icon-btn--danger"
                        :title="$t('common.actions.delete')"
                        :aria-label="$t('common.actions.delete')"
                        :disabled="deletingReviewId === rev.id"
                        @click="confirmAndDeleteReview(rev.id)"
                      >
                        <span v-if="deletingReviewId === rev.id" class="spinner-border spinner-border-sm" aria-hidden="true" />
                        <i v-else class="ph-duotone ph-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <p class="rc-body">{{ rev.comment }}</p>

                <!-- Edit own review in place -->
                <div v-if="canAddReview && rev.isOwnReview && editingReviewId === rev.id" class="rc-edit">
                  <span class="vp-label">{{ $t('profile.reviews.ratingLabel') }}</span>
                  <div class="rc-rating-input">
                    <button
                      v-for="step in ratingSteps"
                      :key="step"
                      type="button"
                      class="rc-star-btn"
                      :class="{ 'is-active': newRating >= step }"
                      :aria-label="$t('profile.reviews.ratingAria', { value: step })"
                      :aria-pressed="newRating === step"
                      @click="newRating = step"
                    >
                      <i class="ph-duotone ph-star"></i>
                    </button>
                    <span class="rc-rating-value">{{ newRating }}/5</span>
                  </div>
                  <label class="vp-label" :for="`rc-edit-${rev.id}`">{{ $t('profile.reviews.commentLabel') }}</label>
                  <textarea
                    :id="`rc-edit-${rev.id}`"
                    v-model="newComment"
                    class="vp-control rc-textarea"
                    rows="3"
                    :placeholder="$t('profile.reviews.commentPlaceholder')"
                    :disabled="submittingReview"
                  />
                  <div class="rc-actions">
                    <button
                      type="button"
                      class="vp-btn vp-btn-ghost vp-btn-sm"
                      :disabled="submittingReview"
                      @click="editingReviewId = null"
                    >
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      type="button"
                      class="vp-btn vp-btn-primary vp-btn-sm"
                      :disabled="submittingReview || newRating < 1 || !newComment.trim()"
                      @click="submitNewReview"
                    >
                      <span v-if="submittingReview" class="spinner-border spinner-border-sm" />
                      {{ $t('common.actions.save') }}
                    </button>
                  </div>
                </div>

                <!-- The supplier's published answers -->
                <div v-for="reply in repliesByReview[rev.id] || []" :key="reply.id" class="rc-reply">
                  <div class="rc-reply-head">
                    <span class="rc-reply-title">{{ $t('profile.reviews.supplierReply') }}</span>
                    <span class="rc-date">{{ reply.createdAtDisplay }}</span>
                  </div>
                  <p class="rc-reply-body">{{ reply.comment }}</p>
                </div>

                <div v-if="canReply" class="rc-reply-compose">
                  <button
                    v-if="replyOpenId !== rev.id"
                    type="button"
                    class="vp-btn vp-btn-ghost vp-btn-sm"
                    @click="openReply(rev.id)"
                  >
                    <i class="ph-duotone ph-arrow-bend-up-left"></i>{{ $t('profile.reviews.reply') }}
                  </button>
                  <template v-else>
                    <textarea
                      v-model="replyText"
                      class="vp-control rc-textarea"
                      rows="2"
                      :aria-label="$t('profile.reviews.replyPlaceholder')"
                      :placeholder="$t('profile.reviews.replyPlaceholder')"
                      :disabled="submittingReplyId === rev.id"
                    />
                    <div class="rc-actions">
                      <button type="button" class="vp-btn vp-btn-ghost vp-btn-sm" @click="cancelReply()">
                        {{ $t('common.cancel') }}
                      </button>
                      <button
                        type="button"
                        class="vp-btn vp-btn-primary vp-btn-sm"
                        :disabled="submittingReplyId === rev.id || !replyText.trim()"
                        @click="submitReply(rev.id)"
                      >
                        <span v-if="submittingReplyId === rev.id" class="spinner-border spinner-border-sm" />
                        {{ $t('common.actions.submit') }}
                      </button>
                    </div>
                  </template>
                </div>
              </article>
            </template>
          </div>
        </template>
      </template>
    </div>

    <div v-if="!isLoading && !loadError && commentsTotalCount > 0" class="vp-card-foot">
      <span>{{ $formatInteger(loadedVisibleCount) }} {{ $t('common.pagination.of') }} {{ $formatInteger(commentsTotalCount) }}</span>
      <button
        v-if="canLoadMore"
        type="button"
        class="vp-btn vp-btn-ghost vp-btn-sm"
        :disabled="isAppending"
        @click="loadMore"
      >
        <span v-if="isAppending" class="spinner-border spinner-border-sm" />
        {{ $t('common.showMore') }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '@/composables/useDateFormatter';
import StarRatingDisplay from '@/components/common/StarRatingDisplay.vue';
import tenantReviewService from '@/services/tenantReviewService';

const BUYER_EDITION_ID = '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0';
const ratingSteps = [1, 2, 3, 4, 5];

const props = defineProps({
  targetTenantId: {
    type: String,
    default: null
  },
  supplierDisplayName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['summaryLoaded']);

const store = useStore();
const { t } = useI18n();
const { formatDateTime } = useDateFormatter();

const currentUser = computed(() => store.getters['auth/currentUser']);

const isBuyerTenant = computed(() => {
  const id = currentUser.value?.editionId?.toUpperCase();
  return id === BUYER_EDITION_ID.toUpperCase();
});

const isViewingOtherTenant = computed(() => {
  const tId = props.targetTenantId;
  const mine = currentUser.value?.tenantId;
  if (!tId || !mine) return false;
  return String(tId).toUpperCase() !== String(mine).toUpperCase();
});

const canAddReview = computed(
  () => isBuyerTenant.value && isViewingOtherTenant.value && summaryMeta.value?.isHaveSalesOrder === true
);

const canReply = computed(() => !isBuyerTenant.value && !isViewingOtherTenant.value);

const isLoading = ref(false);
const commentsLoading = ref(false);
const isAppending = ref(false);
const loadError = ref(null);
const lastReviewItems = ref([]);
const summaryMeta = ref(null);
const commentsTotalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(5);
const sortBy = ref('date_desc');

const newRating = ref(4);
const newComment = ref('');
const submittingReview = ref(false);

const repliesByReview = ref({});
const deletingReviewId = ref(null);
const replyOpenId = ref(null);
const replyText = ref('');
const submittingReplyId = ref(null);
const editingReviewId = ref(null);

function normalizeSummaryResponse(data) {
  if (data == null) {
    return { meta: null };
  }
  const root = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;

  const avgRaw = root.averageRating ?? root.averageScore ?? root.average;
  const meta = {
    averageRating: avgRaw != null && Number.isFinite(Number(avgRaw)) ? Number(avgRaw) : null,
    totalReviewCount:
      root.totalReviewCount != null && Number.isFinite(Number(root.totalReviewCount))
        ? Number(root.totalReviewCount)
        : null,
    isHaveSalesOrder: root.isHaveSalesOrder === true
  };

  const hasMeta = meta.averageRating != null || meta.totalReviewCount != null || meta.isHaveSalesOrder != null;

  return { meta: hasMeta ? meta : null };
}

function normalizeLastReviewsPayload(data) {
  if (data == null) {
    return [];
  }
  const raw = data.items ?? data.reviews ?? (Array.isArray(data) ? data : []);
  if (!Array.isArray(raw)) {
    return [];
  }

  // Flatten nested single-reply objects (API now returns reply inside each review item)
  const flattened = [];
  for (const item of raw) {
    if (!item) continue;
    flattened.push(item);
    if (item.reply && typeof item.reply === 'object') {
      flattened.push({
        ...item.reply,
        parentReviewId: item.id,
        buyerTenantName: item.buyerTenantName || item.buyerName || null
      });
    }
  }
  return flattened;
}

const resolveReviewerLabel = (parentReviewId, reviewerName, buyerName, buyerId) => {
  const fallback = buyerId ? String(buyerId) : '—';
  if (parentReviewId) {
    return props.supplierDisplayName || reviewerName || buyerName || fallback;
  }
  return buyerName || reviewerName || fallback;
};

const normalizeItem = (item) => {
  const id = item?.id || item?.reviewId || null;
  const parentReviewId = item?.parentReviewId || item?.parentId || item?.replyToReviewId || null;
  const tenantId = item?.tenantId || item?.supplierTenantId || item?.targetTenantId || null;
  const reviewerName = item?.reviewerName || item?.createdByUserName || item?.userName || '';
  const rating = item?.rating ?? item?.score ?? null;
  const comment = item?.review || item?.comment || item?.content || item?.message || '';
  const createdAt = item?.reviewDate || item?.creationTime || item?.createdAt || null;
  const isDeleted = item?.isDeleted === true;
  const buyerId = item?.buyerTenantId;
  const buyerName = item?.buyerTenantName ? String(item.buyerTenantName).trim() : '';
  const reviewerLabel = resolveReviewerLabel(parentReviewId, reviewerName, buyerName, buyerId);

  return {
    id,
    parentReviewId,
    tenantId,
    reviewerName,
    buyerTenantName: buyerName || null,
    reviewerLabel,
    rating: rating != null ? Number(rating) : null,
    comment,
    createdAt,
    isDeleted,
    isOwnReview: item?.isOwnReview === true,
    createdAtDisplay: createdAt ? formatDateTime(createdAt) : '-'
  };
};

const topLevelReviews = computed(() => {
  return lastReviewItems.value
    .map(normalizeItem)
    .filter((r) => r.id && !r.parentReviewId && !r.isDeleted);
});

function mapSortToApi(sortValue) {
  switch (sortValue) {
    case 'date_asc':
      return 'date_asc';
    case 'rating_desc':
      return 'rating_desc';
    case 'rating_asc':
      return 'rating_asc';
    case 'date_desc':
    default:
      return 'date_desc';
  }
}

const ownTopLevelReview = computed(() =>
  topLevelReviews.value.find((r) => r.isOwnReview)
);

const pinnedOwnReview = computed(() => ownTopLevelReview.value || null);

const nonOwnTopLevelReviews = computed(() => {
  if (!pinnedOwnReview.value) {
    return topLevelReviews.value;
  }
  return topLevelReviews.value.filter((r) => r.id !== pinnedOwnReview.value.id);
});

const hasOwnTopLevelReview = computed(() => !!ownTopLevelReview.value);

// One list for the template: the buyer's own review pinned first, then everyone else's.
const displayedReviews = computed(() =>
  pinnedOwnReview.value
    ? [{ ...pinnedOwnReview.value, isPinned: true }, ...nonOwnTopLevelReviews.value]
    : nonOwnTopLevelReviews.value
);

const loadedVisibleCount = computed(() => topLevelReviews.value.length);
const canLoadMore = computed(() => loadedVisibleCount.value < commentsTotalCount.value);
function getInitials(name) {
  const value = String(name || '').trim();
  if (!value) return 'NA';
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function buildCommentsPayload() {
  return {
    tenantId: null,
    supplierTenantId: props.targetTenantId,
    sort: mapSortToApi(sortBy.value),
    page: Math.max(0, currentPage.value - 1),
    pageSize: pageSize.value
  };
}

function mergeUniqueReviewItems(existing, incoming) {
  const seen = new Set();
  return [...existing, ...incoming].filter((item) => {
    const id = item?.id || item?.reviewId;
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

async function loadComments({ append = false } = {}) {
  if (!props.targetTenantId) {
    lastReviewItems.value = [];
    commentsTotalCount.value = 0;
    repliesByReview.value = {};
    return;
  }
  const showSpinner = !append && !isLoading.value;
  if (showSpinner) commentsLoading.value = true;
  loadError.value = null;
  try {
    const res = await tenantReviewService.searchTenantReviewComments(buildCommentsPayload());
    const incomingItems = normalizeLastReviewsPayload(res);
    lastReviewItems.value = append
      ? mergeUniqueReviewItems(lastReviewItems.value, incomingItems)
      : incomingItems;
    commentsTotalCount.value = res?.totalCount ?? 0;
    repliesByReview.value = buildRepliesMap(lastReviewItems.value);
  } catch (e) {
    loadError.value = e?.response?.data?.error?.message || t('profile.reviews.loadError');
    if (!append) {
      lastReviewItems.value = [];
      commentsTotalCount.value = 0;
      repliesByReview.value = {};
    }
  } finally {
    if (showSpinner) commentsLoading.value = false;
  }
}

async function loadMore() {
  if (!canLoadMore.value || isAppending.value) {
    return;
  }
  isAppending.value = true;
  currentPage.value += 1;
  try {
    await loadComments({ append: true });
  } finally {
    isAppending.value = false;
  }
}

const averageScore = computed(() => {
  if (summaryMeta.value?.averageRating != null && Number.isFinite(summaryMeta.value.averageRating)) {
    return Math.round(summaryMeta.value.averageRating * 10) / 10;
  }
  const list = topLevelReviews.value.filter((r) => r.rating != null && r.rating >= 1 && r.rating <= 5);
  if (!list.length) return null;
  const sum = list.reduce((s, r) => s + r.rating, 0);
  return Math.round((sum / list.length) * 10) / 10;
});

const hasSummaryStats = computed(() => summaryMeta.value != null);

const showAverageInHeader = computed(() => averageScore.value !== null);

const buildRepliesMap = (items) => {
  const map = {};
  for (const rev of items.map(normalizeItem).filter((r) => r.parentReviewId)) {
    const pid = rev.parentReviewId;
    if (!map[pid]) map[pid] = [];
    map[pid].push(rev);
  }
  for (const k of Object.keys(map)) {
    map[k].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ta - tb;
    });
  }
  return map;
};

const fetchReviews = async () => {
  if (!props.targetTenantId) {
    lastReviewItems.value = [];
    summaryMeta.value = null;
    commentsTotalCount.value = 0;
    return;
  }
  isLoading.value = true;
  loadError.value = null;
  try {
    const summaryRes = await tenantReviewService.getSupplierReviewSummary(props.targetTenantId);
    summaryMeta.value = normalizeSummaryResponse(summaryRes).meta;
    emit('summaryLoaded', summaryRes);
    await loadComments();
    const own = ownTopLevelReview.value;
    if (own) {
      newRating.value = own.rating != null ? own.rating : 4;
      newComment.value = own.comment || '';
    } else {
      newRating.value = 4;
      newComment.value = '';
    }
    editingReviewId.value = null;
  } catch (e) {
    loadError.value = e?.response?.data?.error?.message || t('profile.reviews.loadError');
    lastReviewItems.value = [];
    summaryMeta.value = null;
    commentsTotalCount.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const submitNewReview = async () => {
  if (!props.targetTenantId || newRating.value < 1 || newRating.value > 5) return;
  submittingReview.value = true;
  try {
    await tenantReviewService.createReviewForTenant({
      tenantId: props.targetTenantId,
      rating: newRating.value,
      comment: newComment.value.trim()
    });
    store.dispatch('notify', { message: t('profile.reviews.reviewSaved'), type: 'success' });
    if (!hasOwnTopLevelReview.value) {
      newComment.value = '';
      newRating.value = 4;
    }
    await fetchReviews();
  } catch (e) {
    store.dispatch('notify', {
      message: e?.response?.data?.error?.message || t('profile.reviews.saveError'),
      type: 'error'
    });
  } finally {
    submittingReview.value = false;
  }
};

const openReply = (reviewId) => {
  replyOpenId.value = reviewId;
  replyText.value = '';
};

const cancelReply = () => {
  replyOpenId.value = null;
  replyText.value = '';
};

const submitReply = async (reviewId) => {
  const text = replyText.value.trim();
  if (!text) return;
  submittingReplyId.value = reviewId;
  try {
    await tenantReviewService.createReviewReply({
      reviewId,
      message: text
    });
    store.dispatch('notify', { message: t('profile.reviews.replySaved'), type: 'success' });
    cancelReply();
    await fetchReviews();
  } catch (e) {
    store.dispatch('notify', {
      message: e?.response?.data?.error?.message || t('profile.reviews.replyError'),
      type: 'error'
    });
  } finally {
    submittingReplyId.value = null;
  }
};

const confirmAndDeleteReview = async (reviewId) => {
  if (!reviewId) return;
  const confirmed = window.confirm(t('profile.reviews.deleteConfirm'));
  if (!confirmed) {
    return;
  }
  deletingReviewId.value = reviewId;
  try {
    await tenantReviewService.deleteTenantReview(reviewId);
    store.dispatch('notify', {
      message: t('profile.reviews.deleteSuccess'),
      type: 'success'
    });
    await fetchReviews();
  } catch (e) {
    store.dispatch('notify', {
      message: e?.response?.data?.error?.message || t('profile.reviews.deleteError'),
      type: 'error'
    });
  } finally {
    deletingReviewId.value = null;
  }
};

watch(
  () => props.targetTenantId,
  () => {
    currentPage.value = 1;
    fetchReviews();
  },
  { immediate: true }
);

watch(sortBy, () => {
  currentPage.value = 1;
  if (props.targetTenantId) {
    loadComments({ append: false });
  }
});

defineExpose({ refresh: fetchReviews });
</script>

<style scoped>
/* ── Toolbar ─────────────────────────────────────────────────── */
.rc-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.rc-toolbar .vp-select {
  min-width: 190px;
}

/* ── Compose / edit ──────────────────────────────────────────── */
.rc-compose {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--vp-gray-200);
}

.rc-compose-title {
  margin-bottom: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--vp-heading-color);
}

.rc-edit {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--vp-gray-200);
}

.rc-rating-input {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 10px;
}

.rc-star-btn {
  padding: 2px;
  border: 0;
  background: none;
  font-size: 1.25rem;
  line-height: 1;
  color: var(--vp-gray-300);
  cursor: pointer;
  transition: color 0.12s ease, transform 0.12s ease;
}

.rc-star-btn:hover {
  transform: scale(1.1);
}

.rc-star-btn.is-active {
  color: var(--vp-warning);
}

.rc-star-btn:focus-visible {
  outline: 0;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(var(--vp-primary-rgb), 0.25);
}

.rc-rating-value {
  margin-left: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vp-gray-600);
}

.rc-textarea {
  width: 100%;
  resize: vertical;
}

.rc-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

/* ── List ────────────────────────────────────────────────────── */
.rc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rc-section-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-gray-600);
}

.rc-section-label--split {
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px dashed var(--vp-gray-300);
}

.rc-item {
  padding: 12px 14px;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  background: var(--vp-surface);
}

.rc-item.is-own {
  border-color: rgba(var(--vp-primary-rgb), 0.3);
  background: rgba(var(--vp-primary-rgb), 0.03);
}

.rc-item-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rc-avatar {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: var(--vp-gray-200);
  color: var(--vp-primary);
  font-size: 0.8125rem;
  font-weight: 700;
}

.rc-item-who {
  flex: 1 1 auto;
  min-width: 0;
}

.rc-buyer {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 2px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vp-heading-color);
}

.rc-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.rc-item-actions {
  display: flex;
  gap: 4px;
}

.rc-date {
  font-size: 0.72rem;
  color: var(--vp-gray-500);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.rc-body {
  margin: 10px 0 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: var(--vp-body-color);
  word-break: break-word;
}

/* ── Supplier replies ────────────────────────────────────────── */
.rc-reply {
  margin-top: 10px;
  padding: 10px 12px;
  border-left: 3px solid var(--vp-primary);
  border-radius: 0 var(--vp-radius) var(--vp-radius) 0;
  background: var(--vp-gray-100);
}

.rc-reply-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.rc-reply-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vp-primary);
}

.rc-reply-body {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--vp-body-color);
  word-break: break-word;
}

.rc-reply-compose {
  margin-top: 10px;
}

@media (max-width: 575.98px) {
  .rc-item-head {
    flex-wrap: wrap;
  }

  .rc-item-meta {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
